import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  IconButton,
  Chip,
  Stack,
  Button,
  Collapse,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

import { roundFloatToCents } from '@hydra/internal';
import { toDollarAmount } from '../../../../app-utils';
import { ICreditNoteApplications } from './types';
import InvoiceApplicationRow from './InvoiceApplicationRow';

interface ICreditNoteCardProps {
  creditNoteApplication: ICreditNoteApplications;
  availableCreditAmount: number;
  onToggleExpansion: () => void;
  onUpdateAmount: (creditNoteId: number, invoiceIndex: number, amount: number) => void;
  onRemoveInvoice: (creditNoteId: number, invoiceIndex: number) => void;
  onOpenInvoiceSelection: () => void;
  isSubmitting: boolean;
}

export const CreditNoteCard: React.FC<ICreditNoteCardProps> = ({
  creditNoteApplication,
  availableCreditAmount,
  onToggleExpansion,
  onUpdateAmount,
  onRemoveInvoice,
  onOpenInvoiceSelection,
  isSubmitting,
}) => {
  const totalApplyingAmount = creditNoteApplication.invoiceApplications.reduce(
    (total, invoiceApplication) => total + (invoiceApplication.amount ?? 0),
    0,
  );
  const remainingCreditAmount = availableCreditAmount - totalApplyingAmount;
  // Compare cent-rounded amounts, not raw floats: summing many invoice-application amounts
  // (or credit note totals) as IEEE 754 doubles can leave a sub-cent residue (e.g. -5e-12)
  // that is genuinely zero at currency precision but would otherwise read as "over applied".
  const isOverAvailable = roundFloatToCents(remainingCreditAmount) < 0;

  return (
    <Card sx={{ boxShadow: 1 }}>
      <CardHeader
        onClick={onToggleExpansion}
        sx={{
          cursor: 'pointer',
          bgcolor: '#f8f9fa',
          borderBottom: '1px solid #dee2e6',
          transition: 'background-color 0.15s ease-in-out',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2,
          '&:hover': {
            bgcolor: '#e9ecef',
          },
        }}
        title={
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6" color="primary" fontWeight="bold">
                Credit Note #{creditNoteApplication.creditNote.noteNumber ?? creditNoteApplication.creditNote.id}
              </Typography>
              <Chip
                label={`$${roundFloatToCents(availableCreditAmount)} available`}
                color="success"
                size="small"
              />
              <Chip
                label={`${toDollarAmount(totalApplyingAmount)} applying`}
                color={isOverAvailable ? 'error' : 'primary'}
                size="small"
                variant="outlined"
              />
              <Chip
                label={
                  isOverAvailable
                    ? `${toDollarAmount(Math.abs(remainingCreditAmount))} over`
                    : `${toDollarAmount(remainingCreditAmount)} remaining`
                }
                color={isOverAvailable ? 'error' : 'default'}
                size="small"
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {creditNoteApplication.creditNote.credits?.[0]?.description ?? 'Credit note description'}
            </Typography>
          </Box>
        }
        action={
          <IconButton
            sx={{
              transform: creditNoteApplication.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease-in-out',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        }
      />

      <Collapse in={creditNoteApplication.isExpanded}>
        <CardContent sx={{ bgcolor: '#f5f5f5' }}>
          {creditNoteApplication.invoiceApplications.length > 0 ? (
            <Stack spacing={2}>
              {creditNoteApplication.invoiceApplications.map((invApp, index) => (
                <InvoiceApplicationRow
                  key={index}
                  invApp={invApp}
                  index={index}
                  creditNoteId={creditNoteApplication.creditNoteId}
                  availableCreditAmount={availableCreditAmount}
                  onUpdateAmount={onUpdateAmount}
                  onRemoveApplication={onRemoveInvoice}
                />
              ))}
            </Stack>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Box sx={{ color: 'text.secondary', mb: 2 }}>
                <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {(creditNoteApplication.creditNote.credits?.length ?? 0) > 0
                  ? "No invoices found in the credits for this credit note"
                  : "Credits data not loaded for this credit note"}
              </Box>
            </Box>
          )}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={onOpenInvoiceSelection}
              startIcon={<AddIcon />}
              disabled={isSubmitting}
            >
              Add Invoice
            </Button>
          </Box>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default CreditNoteCard;
